import React, { useEffect, useState } from "react";
import { Anchor, UploadCloud } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  exportPseudoLabels, fehlerText, fetchAnchorPoolStats,
  getPseudoExportStatus, pollJob,
} from "@/lib/api";

const MODE_STYLES = {
  manual: "bg-secondary text-muted-foreground border-border",
  hybrid: "bg-amber-500/10 text-amber-400 border-amber-400/60",
  dl_auto: "bg-primary/10 text-primary border-primary/60",
};

const MODE_LABELS = {
  manual: "manual · Baseline",
  hybrid: "hybrid · Shadow-Mode",
  dl_auto: "dl_auto · DL aktiv",
};

/**
 * Phase 4 Schritt 2 · Data-Flywheel-Fortschritt: „X von Y Ankern für
 * Retraining gesammelt". Speist sich aus dem Dual-Pfad-Export-Pool
 * (GET /api/training/anchor-pool-stats) und zeigt den aktiven
 * HOMOGRAPHY_SOURCE-Rollout-Modus als Badge.
 *
 * 🆕 2026-08-30 · Knopf „Trainingspool aktualisieren".
 *
 * WARUM HIER UND NICHT NUR IM WIZARD
 * Denselben Export gab es schon, aber nur im Anker-Sammelmodus — also
 * hinter Sequenz öffnen → Schritt 3 → Sammelmodus öffnen. Genau die
 * Aktion, die man nach einer Runde Ankerarbeit braucht, lag am
 * unauffindbarsten Ort.
 *
 * WARUM SIE NICHT WEGGELASSEN WERDEN DARF
 * Es gibt zwei Datenpfade, die leicht zu verwechseln sind: der
 * Auto-Harvest schreibt nach jeder Anker-Änderung nach
 * `datasets/line_seg_harvest/` und geht NICHT ins Training. Nur dieser
 * Export lädt in `…/pseudo` und von dort ins Modal-Volume — das ist, was
 * ein Trainingslauf sieht. Wer neue Anker gesetzt hat und sie im nächsten
 * Lauf haben will, muss hier drücken. Siehe DEVELOPMENT.md, Falle 17.
 */
export const AnchorPoolTile = () => {
  const [stats, setStats] = useState(null);
  const [exportiert, setExportiert] = useState(false);
  const [meldung, setMeldung] = useState("");
  const [prozent, setProzent] = useState(0);
  const [ergebnis, setErgebnis] = useState(null);
  // 2026-09-01 · Frische des Volume-Pools (Falle 17 sichtbar): wird
  // mit den Pool-Stats geladen und nach jedem Export neu — die Zeile
  // unten sagt, ob ein Trainingslauf JETZT den aktuellen Stand saehe.
  const [frische, setFrische] = useState(null);
  const laden = () => {
    fetchAnchorPoolStats().then(setStats).catch(() => {});
    getPseudoExportStatus().then(setFrische).catch(() => setFrische(null));
  };
  useEffect(() => { laden(); }, []);

  const trainingspoolAktualisieren = async (overlay = "anchor") => {
    setExportiert(overlay);
    setErgebnis(null);
    setProzent(0);
    setMeldung("Export gestartet …");
    try {
      const { job_id, out_dir } = await exportPseudoLabels(overlay);
      const job = await pollJob(job_id, {
        onProgress: ({ message, progress_pct }) => {
          setMeldung(message || "läuft …");
          setProzent(Number(progress_pct) || 0);
        },
      });
      const r = job.result || {};
      const up = r.modal_upload || {};
      // Ergebnis BLEIBT in der Kachel stehen. Ein Toast ist nach fünf
      // Sekunden weg, der Export dauert aber Minuten — wer währenddessen
      // weggeklickt hat, hätte sonst kein Ergebnis.
      setErgebnis({
        n_written: r.n_written,
        n_uploaded: up.n_files_uploaded,
        overlay,
        n_ohne: r.n_ohne_buendel_pose,
        at: new Date().toLocaleTimeString("de-DE"),
      });
      toast.success(
        `${overlay === "selfcal" ? "Vergleichspool" : "Trainingspool"} `
        + `aktualisiert: ${r.n_written ?? "?"} Samples, `
        + `${up.n_files_uploaded ?? "?"} Dateien → Volume `
        + `${up.remote_path ?? "?"}`
        + (r.n_ohne_buendel_pose
          ? ` · ${r.n_ohne_buendel_pose} Anker ohne Bündel-Pose übersprungen`
          : ""),
      );
      // Neu laden, falls zwischenzeitlich Anker dazukamen. Der Zähler oben
      // ändert sich durch den Export NICHT — er zählt exportfähige Anker,
      // nicht den Inhalt des Pools.
      await laden();
    } catch (e) {
      toast.error(fehlerText(e) || "Export fehlgeschlagen");
    } finally {
      setExportiert(false);
      setMeldung("");
      setProzent(0);
    }
  };

  const n = stats?.n_total_samples ?? 0;
  const target = stats?.target ?? 20;
  const pct = Math.min(100, Math.round((n / target) * 100));
  const mode = stats?.homography_source ?? "manual";

  return (
    <div data-testid="anchor-pool-tile" className="border border-border bg-card rounded-sm p-5">
      <div className="flex items-start justify-between mb-4">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-1.5">
            <Anchor className="w-3 h-3" /> Data-Flywheel · Retraining-Pool
          </div>
          <h3 className="font-display font-black uppercase text-xl tracking-wider mt-1">
            Anker für v1-Retraining
          </h3>
        </div>
        <span
          data-testid="homography-source-badge"
          className={`font-mono text-[10px] uppercase tracking-widest border rounded-sm px-2 py-1 ${MODE_STYLES[mode] || MODE_STYLES.manual}`}
        >
          {MODE_LABELS[mode] || mode}
        </span>
      </div>

      <div className="flex items-baseline gap-2 mb-2">
        <span data-testid="anchor-pool-count" className="font-display font-black text-4xl">
          {stats ? n : "—"}
        </span>
        <span className="font-mono text-sm text-muted-foreground">
          / {target} Ankern gesammelt
        </span>
      </div>
      <div className="h-1.5 bg-secondary rounded-sm overflow-hidden mb-3">
        <div
          data-testid="anchor-pool-progress"
          className={`h-full transition-all ${pct >= 100 ? "bg-primary" : "bg-amber-400"}`}
          style={{ width: `${pct}%` }}
        />
      </div>
      <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground flex flex-wrap gap-x-4 gap-y-1">
        <span data-testid="anchor-pool-homography">
          {stats?.n_homography_samples ?? "—"} Homographie-tauglich (&lt;10 px)
        </span>
        <span data-testid="anchor-pool-seg">
          {stats?.n_seg_samples ?? "—"} Klick-Segmente
        </span>
        <span data-testid="anchor-pool-matches">
          aus {stats?.n_matches ?? "—"} Matches
        </span>
      </div>

      <Button
        data-testid="training-pool-export"
        onClick={trainingspoolAktualisieren}
        disabled={!!exportiert || n === 0}
        className="w-full mt-4 bg-emerald-500 text-black hover:bg-emerald-400 font-display font-bold uppercase tracking-widest disabled:opacity-40"
        title={n === 0
          ? "Noch keine tauglichen Anker — erst Anker setzen"
          : "Exportiert alle tauglichen Anker aller Sequenzen und lädt sie ins Modal-Volume. Erst danach sieht ein Trainingslauf neue Anker."}
      >
        <UploadCloud className="w-4 h-4 mr-2" />
        {exportiert === "anchor" ? (meldung || "Export läuft …")
          : "Trainingspool aktualisieren"}
      </Button>

      {/* 🆕 2026-08-31 · Zweiter Pool für den A/B-Vergleich. Bewusst
          unauffälliger als der Hauptknopf: er ersetzt nichts, er legt
          einen Vergleichsdatensatz daneben. Begründung und Messwerte in
          DEVELOPMENT.md Falle 23 — die Trainingsmasken enthalten alle
          sechs Feldlinien, und die nicht gezeichneten lagen im Median
          67 px daneben (bis 983 px). */}
      <Button
        data-testid="training-pool-export-selfcal"
        onClick={() => trainingspoolAktualisieren("selfcal")}
        disabled={!!exportiert || n === 0}
        variant="outline"
        className="w-full mt-2 font-mono text-[10px] uppercase tracking-widest disabled:opacity-40"
        title={"Zweiter Pool mit Overlays aus dem Bündelausgleich statt aus "
          + "der einzelnen Anker-Homographie. Landet in pseudo_selfcal und "
          + "im Volume unter /pseudo_selfcal — der bestehende Pool bleibt "
          + "unberührt. Gedacht für einen Trainingslauf je Pool, Entscheidung "
          + "über die Shot-Validierung. Anker ohne gegatete Bündel-Pose "
          + "fallen weg statt zurückzufallen, damit die Pools vergleichbar "
          + "bleiben."}
      >
        {exportiert === "selfcal" ? (meldung || "Vergleichspool läuft …")
          : "⚖ Vergleichspool (Selfcal-Overlay)"}
      </Button>
      {exportiert && (
        <div data-testid="training-pool-progress" className="mt-2">
          <div className="h-1.5 bg-secondary rounded-sm overflow-hidden">
            <div
              className="h-full bg-emerald-400 transition-all"
              style={{ width: `${Math.max(2, prozent)}%` }}
            />
          </div>
          <div className="font-mono text-[10px] text-muted-foreground mt-1 leading-relaxed">
            {prozent}% · {meldung}
            {prozent <= 5 && (
              <span className="block text-amber-300">
                Bei 5 % lädt der Export erst die fehlenden Video-Proxys aus B2
                (mehrere hundert MB). Das kann Minuten dauern und ist kein
                Hänger — der zweite Lauf ist deutlich schneller.
              </span>
            )}
          </div>
        </div>
      )}
      {frische && (
        <div
          data-testid="training-pool-frische"
          className={`font-mono text-[10px] mt-2 leading-relaxed ${
            frische.nie_exportiert || frische.stale
              ? "text-amber-300" : "text-emerald-300/80"}`}
        >
          {frische.nie_exportiert ? (
            <>⚠ Noch nie ins Volume exportiert — ein Trainingslauf sähe
            einen leeren/alten Pool.</>
          ) : frische.stale ? (
            <>⚠ Volume-Pool vom {String(frische.export_at).slice(0, 16).replace("T", " ")} Uhr
            ({frische.n_im_pool} Label) ist ÄLTER als {frische.n_geaendert}{" "}
            Video(s): {frische.geaenderte.join(", ")} — vor dem nächsten
            Training exportieren.</>
          ) : (
            <>✓ Volume-Pool aktuell ({frische.n_im_pool} Label, Stand{" "}
            {String(frische.export_at).slice(0, 16).replace("T", " ")} Uhr) —
            das sieht der nächste Trainingslauf.</>
          )}
        </div>
      )}
      {ergebnis && !exportiert && (
        <div
          data-testid="training-pool-ergebnis"
          className="font-mono text-[10px] text-emerald-300 mt-2 leading-relaxed"
        >
          {ergebnis.at}: {ergebnis.n_written ?? "?"} Samples exportiert ·{" "}
          {ergebnis.n_uploaded ?? "?"} Dateien ins Modal-Volume geladen.
        </div>
      )}
      <div className="font-mono text-[10px] text-muted-foreground mt-2 leading-relaxed">
        Der Auto-Harvest nach jeder Anker-Änderung sichert nur lokal. Erst
        dieser Export lädt ins Modal-Volume — das ist, was ein Trainingslauf
        sieht. <span className="text-foreground/70">Die Zahl oben steigt dadurch
        nicht:</span> sie zählt exportfähige Anker, nicht den Inhalt des Pools.
      </div>
    </div>
  );
};

export default AnchorPoolTile;
